import React, { useEffect, useState } from 'react';
import { Check, AlertTriangle, Shield, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { createPayment, redirectToPayment } from '../../services/paymentService';
export const PricingSection = () => {
  const {
    user
  } = useAuth();
  const [isPro, setIsPro] = useState(false);
  const [loadingProStatus, setLoadingProStatus] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Fetch is_pro status from profiles table
  useEffect(() => {
    async function fetchProStatus() {
      if (!user?.id) {
        setLoadingProStatus(false);
        return;
      }
      try {
        const {
          data,
          error
        } = await supabase.from('profiles').select('is_pro').eq('id', user.id).single();
        if (error) throw error;
        setIsPro(data?.is_pro || false);
      } catch (error) {
        console.error('Error fetching Pro status:', error);
        setIsPro(false);
      } finally {
        setLoadingProStatus(false);
      }
    }
    fetchProStatus();
  }, [user?.id]);
  const handlePayment = async (plan: 'Basic' | 'Unlimited') => {
    if (!user?.email || !user?.id) {
      setError('You must be logged in to purchase');
      return;
    }
    setPaymentLoading(plan);
    setError(null);
    try {
      console.log('Initiating payment for plan:', plan);
      // Map plan names to correct plan_type format
      const planType = plan === 'Basic' ? 'single' : 'pro';
      // Use centralized payment service with proper auth
      const response = await createPayment(planType as 'single' | 'pro');
      console.log('Payment created, redirecting to:', response.authorization_url);
      // Redirect to Paystack
      redirectToPayment(response.authorization_url);
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment system is warming up, please try again');
      setPaymentLoading(null);
    }
  };
  return <section className="py-24 bg-[#050505] relative" id="pricing" data-id="element-2885">
      {/* Caution Tape Divider */}
      <div className="absolute top-0 left-0 w-full h-8 bg-[#FFD700] flex overflow-hidden items-center shadow-lg z-10" data-id="element-2886">
        <div className="flex animate-marquee whitespace-nowrap" data-id="element-2887">
          {[...Array(20)].map((_, i) => <span key={i} className="text-black font-black mx-4 uppercase tracking-widest flex items-center gap-2 text-sm" data-id="element-2888">
              <AlertTriangle size={16} fill="black" data-id="element-2889" /> CAUTION: TOXICITY
              DETECTED
            </span>)}
        </div>
      </div>
      <style data-id="element-2890">{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
      `}</style>

      <div className="max-w-6xl mx-auto px-6 pt-16" data-id="element-2891">
        <div className="text-center mb-20" data-id="element-2892">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 uppercase tracking-tight leading-none" data-id="element-2893">
            Invest In Your <span className="text-[#FFD700]" data-id="element-2894">Sanity</span>
          </h2>
          <p className="text-gray-400 text-lg font-mono max-w-2xl mx-auto leading-relaxed" data-id="element-2895">
            Cheaper than therapy. Faster than a breakdown.
          </p>
        </div>

        {/* Error Message */}
        {error && <div className="max-w-4xl mx-auto mb-12 bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 rounded-lg flex items-start gap-3" data-id="element-2896">
            <AlertTriangle className="text-[#FF0000] flex-shrink-0 mt-0.5" size={20} data-id="element-2897" />
            <p className="text-[#FF0000] text-sm font-mono uppercase tracking-wide" data-id="element-2898">
              {error}
            </p>
          </div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-4xl mx-auto items-stretch" data-id="element-2899">
          {/* Free Plan */}
          <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-8 lg:p-10 relative hover:border-gray-600 transition-colors flex flex-col group" data-id="element-2900">
            <div className="absolute top-0 left-0 w-full h-1 bg-gray-700 rounded-t-xl group-hover:bg-gray-500 transition-colors" data-id="element-2901" />

            <div className="flex items-center justify-between mb-8" data-id="element-2902">
              <h3 className="text-2xl font-bold text-white uppercase tracking-wide" data-id="element-2903">
                The "Just Curious"
              </h3>
              <span className="bg-gray-800 text-gray-300 text-xs font-bold px-3 py-1 uppercase tracking-wider rounded border border-gray-700" data-id="element-2904">
                Trial
              </span>
            </div>

            <div className="flex items-baseline mb-8" data-id="element-2905">
              <span className="text-5xl lg:text-6xl font-black text-white" data-id="element-2906">
                $0
              </span>
              <span className="text-gray-500 ml-2 font-mono text-sm uppercase tracking-wide" data-id="element-2907">
                / forever
              </span>
            </div>

            <ul className="space-y-4 mb-10 flex-1" data-id="element-2908">
              <PricingFeature text="1 Free Forensic Scan" data-id="element-2909" />
              <PricingFeature text="Basic Toxicity Detection" data-id="element-2910" />
              <PricingFeature text="Teaser Results Card" data-id="element-2911" />
              <PricingFeature text="Standard Processing Speed" data-id="element-2912" />
            </ul>

            <a href="/signup" className="block w-full bg-gray-800 text-white text-center py-4 font-bold uppercase tracking-wider hover:bg-gray-700 transition-colors border border-gray-700 rounded-xl text-sm" data-id="element-2913">
              Start Free Scan
            </a>
          </div>

          {/* Pro Plan */}
          <div className="bg-black border-2 border-[#FF0000] rounded-xl p-8 lg:p-10 relative shadow-[0_0_40px_rgba(255,0,0,0.15)] flex flex-col z-10" data-id="element-2914">
            {/* Discount Badge */}
            <div className="absolute -top-4 right-6 bg-[#FF0000] text-white font-black px-4 py-1.5 uppercase tracking-wider shadow-lg text-xs border border-black transform rotate-2" data-id="element-2915">
              65% OFF
            </div>

            <div className="flex items-center justify-between mb-2 mt-2" data-id="element-2916">
              <h3 className="text-2xl font-black text-[#FF0000] uppercase tracking-wide" data-id="element-2917">
                The "Truth Seeker"
              </h3>
              <Shield className="text-[#FF0000]" size={28} data-id="element-2918" />
            </div>

            <div className="flex items-baseline mb-2" data-id="element-2919">
              <span className="text-5xl lg:text-6xl font-black text-white" data-id="element-2920">
                $9.99
              </span>
              <span className="text-gray-400 ml-2 font-mono text-sm uppercase tracking-wide" data-id="element-2921">
                / month
              </span>
            </div>
            <p className="text-[#FFD700] text-xs font-bold mb-8 uppercase tracking-wide" data-id="element-2922">
              Normally $29.99/mo
            </p>

            <div className="space-y-4 mb-10 flex-1" data-id="element-2923">
              <div className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 mb-6 rounded-lg" data-id="element-2924">
                <p className="text-[#FF0000] text-[10px] font-bold uppercase mb-3 tracking-widest" data-id="element-2925">
                  INCLUDES "THE SHIELD" MONITORING
                </p>
                <ul className="space-y-3" data-id="element-2926">
                  <PricingFeature text="Unlimited Forensic Scans" highlight data-id="element-2927" />
                  <PricingFeature text="Persona Evolution Tracking" highlight data-id="element-2928" />
                  <PricingFeature text="Monthly Clarity Reports" highlight data-id="element-2929" />
                  <PricingFeature text="Gaslight Alert System" highlight data-id="element-2930" />
                </ul>
              </div>
              <PricingFeature text="Priority Processing Queue" data-id="element-2931" />
              <PricingFeature text="Full PDF Case Files" data-id="element-2932" />
            </div>

            <button onClick={() => handlePayment('Unlimited')} disabled={paymentLoading !== null} className="w-full bg-[#FF0000] text-white py-4 font-black uppercase tracking-wider hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(255,0,0,0.4)] hover:shadow-[0_0_30px_rgba(255,0,0,0.6)] rounded-xl hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm" data-id="element-2933">
              {paymentLoading === 'Unlimited' ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2934" />
                  <span data-id="element-2935">Processing...</span>
                </> : <>
                  <Shield size={16} fill="white" data-id="element-2936" />
                  <span data-id="element-2937">Get Full Access</span>
                </>}
            </button>

            <div className="mt-6 text-center" data-id="element-2938">
              <span className="text-[10px] text-gray-500 font-mono flex items-center justify-center gap-1.5 uppercase tracking-wide" data-id="element-2939">
                <Lock size={10} data-id="element-2940" /> 30-Day Money Back Guarantee
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const PricingFeature = ({
  text,
  highlight = false
}: {
  text: string;
  highlight?: boolean;
}) => {
  return <li className="flex items-start gap-3" data-id="element-2941">
      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5" data-id="element-2942">
        <Check size={16} className={highlight ? 'text-[#FF0000]' : 'text-gray-500'} strokeWidth={3} data-id="element-2943" />
      </div>
      <span className={`text-sm font-medium ${highlight ? 'text-white' : 'text-gray-400'}`} data-id="element-2944">
        {text}
      </span>
    </li>;
};